import { HandPointing, PencilSimple, Trash } from '@phosphor-icons/react';
import { Button, Icon, Sheet, Text } from '@shh/ui';
import { createContext, useContext, useState, type ReactNode } from 'react';
import { useLens } from '../app/lens';
import { deleteTask, deleteTodo } from '../data/api';
import { useFamily } from '../data/family';
import { canEditJob, type Job } from '../data/jobs';
import { JobForm } from './taskForms';
import { tasksText } from '../i18n/tasks';
import { markTipSeen, useTip } from '../lib/tips';

type Step = 'menu' | 'edit' | 'delete';
type Open = (job: Job, step?: Step) => void;
const JobEditContext = createContext<Open | null>(null);

/**
 * The card's "Bearbeiten · Löschen" (1.28, concept 24.10.): long press on phones and tablets,
 * the pencil on a PC. Returns the handler for a job, or nothing when the viewer may not change it.
 */
export function useJobEdit() {
  const open = useContext(JobEditContext);
  const f = useFamily();
  const lens = useLens();
  return (job: Job, step?: Step) =>
    open && canEditJob(f, lens.canParent, job) ? () => open(job, step) : undefined;
}

/** "Lange drücken zum Bearbeiten" – once, for those who may change jobs. */
export function JobEditTip() {
  const f = useFamily();
  const lens = useLens();
  const tip = useTip('jobEdit');
  if (!tip.show || !(lens.canParent || f.role === 'teen')) return null;
  return (
    <p className="flex flex-wrap items-center gap-gap-sm rounded-control bg-surface-sunken p-inset-xs text-sm font-semibold text-text-secondary">
      <Icon icon={HandPointing} size="sm" className="text-accent-primary" />
      <span className="flex-1">{tasksText.holdTip}</span>
      <Button variant="quiet" onPress={tip.dismiss}>
        {tasksText.gotIt}
      </Button>
    </p>
  );
}

/** One menu sheet for every job card in the app (Heute, Aufgaben, Personenseite, Kalender). */
export function JobEditProvider({ children }: { children: ReactNode }) {
  const f = useFamily();
  const [job, setJob] = useState<Job>();
  const [step, setStep] = useState<Step>('menu');
  const close = () => {
    setJob(undefined);
    setStep('menu');
  };
  const title = job?.kind === 'task' ? job.task.title : (job?.todo.title ?? '');
  const remove = () => {
    if (!job) return;
    void (job.kind === 'task'
      ? deleteTask(f.householdId, job.task.id)
      : deleteTodo(f.householdId, job.todo.id));
    close();
  };
  return (
    <JobEditContext.Provider
      value={(j, s = 'menu') => {
        markTipSeen('jobEdit');
        setStep(s);
        setJob(j);
      }}
    >
      {children}
      <Sheet
        isOpen={!!job}
        onOpenChange={(o) => !o && close()}
        title={step === 'edit' ? tasksText.editJob : title}
        isDismissable={step !== 'edit'}
      >
        {job && step === 'edit' && (
          <JobForm
            onDone={close}
            task={job.kind === 'task' ? job.task : undefined}
            todo={job.kind === 'todo' ? job.todo : undefined}
          />
        )}
        {job && step === 'menu' && (
          <div className="flex flex-col gap-gap-sm">
            <Button icon={PencilSimple} onPress={() => setStep('edit')}>
              {tasksText.edit}
            </Button>
            <Button variant="secondary" icon={Trash} onPress={() => setStep('delete')}>
              {tasksText.remove}
            </Button>
          </div>
        )}
        {job && step === 'delete' && (
          <div className="flex flex-col gap-gap-md">
            <Text>{tasksText.removeAsk}</Text>
            <div className="flex flex-wrap gap-gap-sm">
              <Button variant="danger" icon={Trash} onPress={remove}>
                {tasksText.removeYes}
              </Button>
              <Button variant="secondary" onPress={close}>
                {tasksText.keep}
              </Button>
            </div>
          </div>
        )}
      </Sheet>
    </JobEditContext.Provider>
  );
}
